// btc15/pulse.js — what the one-second lane says, and how to trust it.
//
// /api/btc15/pulse carries the live Kalshi book (one shared call a second on
// the server), this window's wagers, and the last half hour of buys and
// sells. The tab used to read all of that from /now: a 5 s poll of a block the
// desk only rewrites every 20 s. These are the pure rules for laying the two
// together, so both the phone tab and the desktop tab agree, and so they can
// be tested without a browser.

// A pulse older than this is not "live" any more: the tab falls back to /now
// rather than show a price it cannot vouch for.
export const PULSE_FRESH_MS = 5000;
// How long a buy or sell keeps its agent's dot flashing.
export const FLASH_MS = 12000;

const EMPTY = [];

export const emptyPulse = { data: null, receivedAt: null, ok: true, failures: 0, rttMs: null };

// The pulse, only if it is about the window `now` is showing and recent
// enough to trust. Anything else is null, and callers read /now instead.
export function usablePulse(now, pulseState, nowMs = Date.now()) {
  const d = pulseState?.data;
  if (!d || !d.ticker || pulseState.receivedAt == null) return null;
  if (now?.window?.ticker && d.ticker !== now.window.ticker) return null;   // the window rolled; /now has not caught up yet
  if (nowMs - pulseState.receivedAt > PULSE_FRESH_MS) return null;
  return d;
}

// The prices and wagers the tab should draw right now.
export function mergeLive(now, pulseState, nowMs = Date.now()) {
  const live = now?.live || {};
  const p = usablePulse(now, pulseState, nowMs);
  if (!p) {
    return { yesBid: live.yesBid, yesAsk: live.yesAsk, marketP: live.marketP, book: live.book,
             wagers: now?.wagers || EMPTY, fromPulse: false, source: "poll", quoteAgeMs: null };
  }
  const aged = p.quoteAgeMs == null ? null : p.quoteAgeMs + Math.max(0, nowMs - pulseState.receivedAt);
  return { yesBid: p.yesBid ?? live.yesBid, yesAsk: p.yesAsk ?? live.yesAsk, marketP: p.marketP ?? live.marketP,
           book: p.book ?? live.book, wagers: p.wagers || EMPTY, fromPulse: true, source: p.quoteSource || "orderbook", quoteAgeMs: aged };
}

// The server's clock as best the tab can tell: what the server said when the
// pulse arrived, plus the time since. It never reads the device's own clock
// against a server timestamp, so a phone that is a minute off cannot make a
// fresh trade look old.
export function serverNowMs(pulseState, nowMs = Date.now()) {
  const d = pulseState?.data;
  if (!d?.serverAt || pulseState.receivedAt == null) return nowMs;
  return Date.parse(d.serverAt) + Math.max(0, nowMs - pulseState.receivedAt);
}

export const eventAgeMs = (event, pulseState, nowMs = Date.now()) => {
  const at = Date.parse(event?.at);
  return Number.isFinite(at) ? Math.max(0, serverNowMs(pulseState, nowMs) - at) : Infinity;
};

// Each agent's most recent event. Events arrive newest first.
export function lastEventByAgent(events) {
  const out = {};
  for (const e of events || EMPTY) if (e.agentKey && !out[e.agentKey]) out[e.agentKey] = e;
  return out;
}

// What an agent's dot should say: "buy" or "sell" (a settle counts as a sell)
// while the event is young, otherwise the agent's standing state.
export function agentDot(event, openCount, pulseState, nowMs = Date.now()) {
  if (event && eventAgeMs(event, pulseState, nowMs) < FLASH_MS) {
    return { state: event.kind === "buy" ? "buy" : "sell", flashing: true, event };
  }
  return { state: openCount > 0 ? "in" : "out", flashing: false, event: event || null };
}

// One line for the activity feed.
export function eventLine(e, nameOf, money, cents) {
  const who = nameOf(e.agentKey);
  const side = e.side === "yes" ? "Up" : "Down";
  if (e.kind === "buy") return { who, verb: `bought ${side}`, detail: `@ ${cents(e.price)}`, tone: "buy" };
  const why = (e.reason || "").replace(/_/g, " ");
  if (e.kind === "settle") return { who, verb: e.won ? `won ${side}` : `lost ${side}`, detail: `at the bell · ${money(e.pnl)}`, tone: e.won ? "win" : "loss" };
  return { who, verb: `sold ${side}`, detail: `@ ${cents(e.price)}${why ? ` · ${why}` : ""} · ${money(e.pnl)}`, tone: Number(e.pnl) >= 0 ? "win" : "loss" };
}
